import React from "react";
import { alpha, createTheme } from "@mui/material/styles";
import { createCssVariables, remToPx, uiTokens } from "./uiTokens";

export { uiTokens };

const legacyBlue = "#448BCA";
/** English UI – primary stack Outfit */
export const fontFamilyBase = "var(--font-family-base, Outfit, Tajawal, Arial, sans-serif)";
/** Arabic UI – primary stack Tajawal */
export const fontFamilyBaseAr = "var(--font-family-base, Tajawal, Outfit, Arial, sans-serif)";
const inputHeight = (size) => (size === "large" ? "3rem" : "2.5rem");
const inputRadius = uiTokens.radius.md;
const inputPaddingY = "0.5rem";
const inputPaddingX = "0.75rem";

const disabledContained = (main, contrast = "#ffffff", bgOpacity = 0.38) => ({
  "&.Mui-disabled": {
    color: contrast,
    background: alpha(main, bgOpacity),
    backgroundColor: alpha(main, bgOpacity),
    borderColor: "transparent",
  },
});

const disabledOutlined = (main) => ({
  "&.Mui-disabled": {
    color: alpha(main, 0.55),
    borderColor: alpha(main, 0.45),
    backgroundColor: "transparent",
  },
});

const checkboxIcon = <span className="ui-theme-checkbox-icon" />;
const checkboxCheckedIcon = (
  <span className="ui-theme-checkbox-icon ui-theme-checkbox-icon--checked">
    <svg viewBox="0 0 14 11" aria-hidden="true" focusable="false">
      <path d="M1 5.5L5 9.5L13 1.5" />
    </svg>
  </span>
);
const checkboxIndeterminateIcon = (
  <span className="ui-theme-checkbox-icon ui-theme-checkbox-icon--indeterminate">
    <span className="ui-theme-checkbox-dash" />
  </span>
);

const Theme = createTheme({
  palette: {
    mode: "light",
    primary: {
      main: uiTokens.color.semantic.primary.default,
      dark: uiTokens.color.semantic.primary.active,
      light: uiTokens.color.semantic.primary.subtle,
      contrastText: "#ffffff",
    },
    secondary: {
      main: uiTokens.color.semantic.fuchsia.default,
      contrastText: "#ffffff",
    },
    success: {
      main: uiTokens.color.semantic.success.default,
      dark: uiTokens.color.semantic.success.active,
      light: uiTokens.color.primitive.success[100],
      contrastText: "#ffffff",
    },
    error: {
      main: uiTokens.color.semantic.destructive.default,
      dark: uiTokens.color.semantic.destructive.hover,
      light: uiTokens.color.primitive.error[100],
      contrastText: "#ffffff",
    },
    warning: {
      main: uiTokens.color.semantic.warning.default,
      contrastText: uiTokens.color.semantic.foreground.default,
    },
    info: {
      main: uiTokens.color.app.info.default,
      contrastText: "#ffffff",
    },
    background: {
      default: uiTokens.color.semantic.muted.default,
      paper: uiTokens.color.semantic.background.default,
    },
    text: {
      primary: uiTokens.color.semantic.foreground.default,
      secondary: uiTokens.color.semantic.foreground.muted,
    },
    divider: uiTokens.color.semantic.border.default,
    mainBlue: {
      main: legacyBlue,
      contrastText: "#ffffff",
    },
    grayIcon: {
      main: "#6F767E",
    },
  },
  shape: {
    borderRadius: Number.parseFloat(uiTokens.radius.lg),
  },
  spacing: 8,
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        ":root": createCssVariables(),
        body: {
          backgroundColor: uiTokens.color.semantic.muted.default,
        },
      },
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          textTransform: "none",
          boxShadow: "none",
          borderRadius: uiTokens.radius.md,
          fontWeight: uiTokens.fontWeight.medium,
          minHeight: "2rem",
          letterSpacing: 0,
          display: "flex",
          gap: uiTokens.space[8],
          "&:hover": {
            boxShadow: "none",
          },
          "&:active": {
            boxShadow: "none",
          },
        },
        sizeSmall: {
          minHeight: uiTokens.space[32],
          height: uiTokens.space[32],
          paddingLeft: uiTokens.component.button.paddingX.sm,
          paddingRight: uiTokens.component.button.paddingX.sm,
          fontSize: uiTokens.typography.label.sm.fontSize,
          fontWeight: uiTokens.typography.label.sm.fontWeight,
          lineHeight: uiTokens.typography.label.sm.lineHeight,
        },
        sizeMedium: {
          minHeight: uiTokens.space[40],
          height: uiTokens.space[40],
          paddingLeft: uiTokens.component.button.paddingX.md,
          paddingRight: uiTokens.component.button.paddingX.md,
          fontSize: uiTokens.typography.label.md.fontSize,
          fontWeight: uiTokens.typography.label.md.fontWeight,
          lineHeight: uiTokens.typography.label.md.lineHeight,
        },
        sizeLarge: {
          minHeight: uiTokens.space[48],
          height: uiTokens.space[48],
          paddingLeft: uiTokens.component.button.paddingX.md,
          paddingRight: uiTokens.component.button.paddingX.md,
          fontSize: uiTokens.typography.label.lg.fontSize,
          fontWeight: uiTokens.typography.label.lg.fontWeight,
          lineHeight: uiTokens.typography.label.lg.lineHeight,
        },
        containedPrimary: {
          background: `linear-gradient(90deg, ${uiTokens.color.semantic.primary.default} 0%, ${uiTokens.color.semantic.fuchsia.default} 100%)`,
          "&:hover": {
            background: `linear-gradient(90deg, ${uiTokens.color.semantic.primary.hover} 0%, ${uiTokens.color.primitive.fuchsia[600]} 100%)`,
          },
          "&:active": {
            background: `linear-gradient(90deg, ${uiTokens.color.semantic.primary.active} 0%, ${uiTokens.color.primitive.fuchsia[700]} 100%)`,
          },
          ...disabledContained(uiTokens.color.semantic.primary.default),
        },
        containedMainBlue: disabledContained(legacyBlue),
        containedSecondary: disabledContained(uiTokens.color.semantic.fuchsia.default),
        containedSuccess: disabledContained(uiTokens.color.semantic.success.default),
        containedError: disabledContained(uiTokens.color.semantic.destructive.default),
        containedWarning: disabledContained(uiTokens.color.semantic.warning.default, "#ffffff"),
        containedInfo: disabledContained(uiTokens.color.app.info.default),
        outlinedPrimary: {
          color: uiTokens.color.semantic.primary.active,
          borderColor: uiTokens.color.semantic.primary.default,
          "&:hover": {
            borderColor: uiTokens.color.semantic.primary.active,
            backgroundColor: alpha(uiTokens.color.semantic.primary.default, 0.06),
          },
          ...disabledOutlined(uiTokens.color.semantic.primary.default),
        },
        outlinedMainBlue: disabledOutlined(legacyBlue),
        outlinedSecondary: disabledOutlined(uiTokens.color.semantic.fuchsia.default),
        outlinedSuccess: disabledOutlined(uiTokens.color.semantic.success.default),
        outlinedError: disabledOutlined(uiTokens.color.semantic.destructive.default),
        outlinedWarning: disabledOutlined(uiTokens.color.semantic.warning.default),
        outlinedInfo: disabledOutlined(uiTokens.color.app.info.default),
        textPrimary: {
          "&.Mui-disabled": {
            color: alpha(uiTokens.color.semantic.primary.default, 0.45),
          },
        },
        textMainBlue: {
          "&.Mui-disabled": {
            color: alpha(legacyBlue, 0.45),
          },
        },
        textSuccess: {
          "&.Mui-disabled": {
            color: alpha(uiTokens.color.semantic.success.default, 0.45),
          },
        },
        textError: {
          "&.Mui-disabled": {
            color: alpha(uiTokens.color.semantic.destructive.default, 0.45),
          },
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: ({ theme }) => ({
          minHeight: "3.875rem",
          padding: "0.875rem 1.25rem 0.875rem 1.5rem",
          borderRadius: uiTokens.radius.lg,
          alignItems: "center",
          fontFamily: theme.typography.fontFamily,
          boxShadow: "none",
          letterSpacing: 0,
          "& .ui-alert__icon": {
            // width: "2rem",
            // height: "2rem",
            backgroundColor: uiTokens.color.semantic.background.default,
            borderRadius: uiTokens.radius.full,
            border: "1px solid currentColor",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            opacity: 1,
          },
          "& .ui-alert__icon .MuiSvgIcon-root": {
            width: "1rem",
            height: "1rem",
            fontSize: "1rem",
          },
          "& .ui-alert__subtitle": {
            color: uiTokens.color.semantic.foreground.default,
            fontFamily: theme.typography.fontFamily,
            fontSize: "0.75rem",
            fontWeight: 400,
            lineHeight: "1rem",
            letterSpacing: 0,
            verticalAlign: "middle",
          },
        }),
        standardSuccess: {
          color: uiTokens.color.semantic.success.default,
          border: `1px solid ${uiTokens.color.primitive.success[100]}`,
          backgroundColor: uiTokens.color.semantic.success.subtle,
          "& .MuiAlertTitle-root": {
            color: uiTokens.color.semantic.success.default,
          },
          "& .MuiAlert-icon .ui-alert__icon": {
            borderColor: uiTokens.color.primitive.success[100],
            color: uiTokens.color.semantic.success.default,
          },
        },
        standardError: {
          color: uiTokens.color.semantic.destructive.default,
          border: `1px solid ${uiTokens.color.semantic.destructive.border}`,
          backgroundColor: uiTokens.color.semantic.destructive.subtle,
          "& .MuiAlertTitle-root": {
            color: uiTokens.color.semantic.destructive.default,
          },
          "& .MuiAlert-icon .ui-alert__icon": {
            borderColor: uiTokens.color.semantic.destructive.border,
            color: uiTokens.color.semantic.destructive.default,
          },
        },
        icon: {
          marginRight: "1rem",
          padding: 0,
          color: "inherit",
          opacity: 1,
          alignItems: "center",
        },
        message: {
          padding: 0,
          minWidth: 0,
        },
        action: {
          marginRight: 0,
          padding: 0,
          alignSelf: "center",
          color: uiTokens.color.semantic.foreground.default,
          "& .MuiIconButton-root": {
            width: "2rem",
            height: "2rem",
            padding: 0,
            color: "inherit",
          },
          "& .MuiSvgIcon-root": {
            width: "14px",
            height: "14px",
            fontSize: "14px",
          },
        },
      },
    },
    MuiAlertTitle: {
      styleOverrides: {
        root: ({ theme }) => ({
          margin: 0,
          fontFamily: theme.typography.fontFamily,
          fontSize: "0.875rem",
          fontWeight: 700,
          lineHeight: "1.25rem",
          letterSpacing: 0,
          verticalAlign: "middle",
          "& + .ui-alert__subtitle": {
            marginTop: 0,
          },
        }),
      },
    },
    MuiTextField: {
      defaultProps: {
        variant: "outlined",
        fullWidth: true,
        size: "medium",
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: ({ ownerState }) => ({
          minHeight: inputHeight(ownerState.size),
          borderRadius: inputRadius,
          backgroundColor: uiTokens.color.semantic.background.default,
          alignItems: "center",
          transition: "border-color 160ms ease, box-shadow 160ms ease",
          ...(ownerState.startAdornment && {
            paddingInlineStart: 14,
          }),
          ...(ownerState.endAdornment && {
            paddingInlineEnd: 14,
          }),
          ".ui-input &": {
            gap: "1rem",
            height: inputHeight(ownerState.size),
          },
          "&:hover:not(.Mui-disabled) .MuiOutlinedInput-notchedOutline": {
            borderColor: uiTokens.color.semantic.primary.default,
          },
          "&:hover:not(.Mui-disabled)": {
            boxShadow: `0 0 0 2px ${alpha(uiTokens.color.semantic.primary.default, 0.2)}`,
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: uiTokens.color.semantic.primary.default,
            borderWidth: 1,
          },
          "&.Mui-focused": {
            boxShadow: `0 0 0 2px ${alpha(uiTokens.color.semantic.primary.default, 0.2)}`,
          },
          ".ui-input--filled &:not(.Mui-focused):not(:hover):not(.Mui-disabled) .MuiOutlinedInput-notchedOutline": {
            borderColor: uiTokens.color.semantic.foreground.subtle,
          },
          "&.Mui-disabled": {
            backgroundColor: uiTokens.color.semantic.background.default,
            borderRadius: inputRadius,
            cursor: "default",
            opacity: 1,
            pointerEvents: "none",
          },
          "&.Mui-disabled .MuiOutlinedInput-notchedOutline": {
            borderColor: uiTokens.color.semantic.border.default,
          },
          "&.Mui-disabled .MuiOutlinedInput-input": {
            color: uiTokens.color.semantic.foreground.default,
            cursor: "default",
            padding: `0 ${uiTokens.space[16]}`,
            opacity: 0.30,
            WebkitTextFillColor: alpha(
              uiTokens.color.semantic.foreground.default,
              0.72,
            ),
          },
          "&.Mui-disabled .MuiInputAdornment-root, &.Mui-disabled .MuiSvgIcon-root": {
            color: uiTokens.color.semantic.foreground.default,
          },
          "&.MuiInputBase-multiline": {
            height: "auto",
            minHeight: "auto",
            alignItems: "stretch",
            padding: 0,
          },
          "&.MuiInputBase-adornedStart .MuiOutlinedInput-input": {
            paddingLeft: 0,
          },
          "&.MuiInputBase-adornedEnd .MuiOutlinedInput-input": {
            paddingRight: 0,
          },
          "& .MuiInputAdornment-root": {
            alignSelf: "center",
            marginTop: 0,
          },
          "& .MuiInputAdornment-positionStart": {
            marginInlineEnd: "0.625rem",
          },
          "& .MuiInputAdornment-positionEnd": {
            marginInlineStart: "0.625rem",
          },
          ".ui-input & .MuiInputAdornment-positionStart, .ui-input & .MuiInputAdornment-positionEnd":
            {
              margin: 0,
            },
          "&.Mui-error": {
            boxShadow: `0 0 0 2px ${alpha(uiTokens.color.semantic.destructive.default, 0.2)}`,
          },
          "&.Mui-error .MuiOutlinedInput-notchedOutline": {
            borderColor: `${uiTokens.color.semantic.destructive.default} !important`,
            borderWidth: 1,
          },
          "&.Mui-error:hover:not(.Mui-disabled) .MuiOutlinedInput-notchedOutline":
            {
              borderColor: `${uiTokens.color.semantic.destructive.default} !important`,
            },
          "&.Mui-error.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: `${uiTokens.color.semantic.destructive.default} !important`,
            borderWidth: 1,
          },
        }),
        notchedOutline: {
          borderColor: uiTokens.color.semantic.border.default,
        },
        input: {
          boxSizing: "border-box",
          color: uiTokens.color.semantic.foreground.muted,
          fontSize: "0.875rem",
          fontWeight: 400,
          lineHeight: "1.25rem",
          padding: `${inputPaddingY} ${inputPaddingX}`,
          "&::placeholder": {
            color: uiTokens.color.semantic.foreground.muted,
            opacity: "70%",
          },
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        inputRoot: {
          minHeight: inputHeight(),
          borderRadius: inputRadius,
          alignItems: "center",
          paddingTop: "0 !important",
          paddingBottom: "0 !important",
          paddingLeft: inputPaddingX,
          paddingRight: inputPaddingX,
        },
        input: {
          padding: "0 !important",
        },
        endAdornment: {
          insetInlineEnd: inputPaddingX,
        },
        popupIndicator: {
          padding: 0,
        },
        clearIndicator: {
          padding: 0,
          marginInlineEnd: "0.5rem",
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: uiTokens.color.semantic.foreground.muted,
          fontWeight: 600,
          letterSpacing: 0,
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          marginLeft: 0,
          marginRight: 0,
          fontSize: "0.75rem",
          lineHeight: 1.2,
          // MUI applies higher specificity for the error state; override via root + &.Mui-error
          // instead of a top-level `error` slot (see MUI console warning).
          "&.Mui-error": {
            color: uiTokens.color.semantic.destructive.default,
            fontSize: uiTokens.typography.body.sm.fontSize,
            fontWeight: uiTokens.typography.body.sm.fontWeight,
            fontStyle: "normal",
            lineHeight: uiTokens.typography.body.sm.lineHeight,
            letterSpacing: 0,
          },
        },
        contained: {
          marginLeft: 0,
          marginRight: 0,
        },
      },
    },
    MuiCheckbox: {
      defaultProps: {
        icon: checkboxIcon,
        checkedIcon: checkboxCheckedIcon,
        indeterminateIcon: checkboxIndeterminateIcon,
      },
      styleOverrides: {
        root: {
          height: 20,
          padding: 0,
          width: 20,
          "& .ui-theme-checkbox-icon": {
            alignItems: "center",
            background: "var(--color-table-row2, #FFFFFF)",
            border: "2px solid var(--color-foreground-subtle, #94A3B8)",
            borderRadius: 4,
            boxSizing: "border-box",
            display: "inline-flex",
            height: 20,
            justifyContent: "center",
            opacity: 1,
            width: 20,
          },
          "& .ui-theme-checkbox-icon--checked, & .ui-theme-checkbox-icon--indeterminate": {
            background: "var(--color-primary-default, #9645FF)",
            borderColor: "var(--color-primary-default, #9645FF)",
          },
          "& .ui-theme-checkbox-icon--checked svg": {
            height: 11,
            width: 14,
          },
          "& .ui-theme-checkbox-icon--checked path": {
            fill: "none",
            stroke: uiTokens.color.semantic.background.default,
            strokeLinecap: "round",
            strokeLinejoin: "round",
            strokeWidth: 2,
          },
          "& .ui-theme-checkbox-dash": {
            background: uiTokens.color.semantic.background.default,
            borderRadius: uiTokens.radius.full,
            display: "inline-block",
            height: 2,
            width: 10,
          },
        },
      },
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          "&.ui-radio": {
            height: 20,
            padding: 0,
            width: 20,
            "& .ui-theme-radio-icon": {
              alignItems: "center",
              backgroundColor: uiTokens.color.semantic.background.default,
              border: `2px solid ${uiTokens.color.semantic.foreground.subtle}`,
              borderRadius: uiTokens.radius.full,
              boxSizing: "border-box",
              display: "inline-flex",
              height: 20,
              justifyContent: "center",
              width: 20,
            },
            "& .ui-theme-radio-icon--checked": {
              borderColor: uiTokens.color.semantic.primary.default,
            },
            "& .ui-theme-radio-dot": {
              backgroundColor: uiTokens.color.semantic.primary.default,
              borderRadius: uiTokens.radius.full,
              height: 10,
              width: 10,
            },
            "&.Mui-disabled .ui-theme-radio-icon": {
              backgroundColor: uiTokens.color.semantic.background.default,
              borderColor: uiTokens.color.semantic.border.default,
            },
            "&.Mui-disabled .ui-theme-radio-icon--checked": {
              borderColor: uiTokens.color.semantic.primary.subtle,
            },
            "&.Mui-disabled .ui-theme-radio-dot": {
              backgroundColor: uiTokens.color.semantic.primary.subtle,
            },
          },
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        root: {
          "&.ui-toggle": {
            width: "2.125rem",
            height: "1.25rem",
            padding: 0,
            overflow: "visible",
            display: "inline-flex",
            alignItems: "center",
            flexShrink: 0,
            "& .MuiSwitch-switchBase": {
              padding: 0,
              color: uiTokens.color.semantic.background.default,
              transitionDuration: "180ms",
              "&.Mui-checked": {
                transform: "translateX(0.875rem)",
                color: uiTokens.color.semantic.background.default,
                "& + .MuiSwitch-track": {
                  backgroundColor: uiTokens.color.semantic.primary.default,
                  opacity: 1,
                },
              },
              "&.Mui-focusVisible .MuiSwitch-thumb": {
                boxShadow: `0 0 0 0.1875rem ${alpha(uiTokens.color.semantic.primary.default, 0.24)}`,
              },
              "&.Mui-disabled": {
                "& + .MuiSwitch-track": {
                  backgroundColor: uiTokens.color.semantic.border.default,
                  opacity: 1,
                },
              },
            },
            "& .MuiSwitch-thumb": {
              width: "1.25rem",
              height: "1.25rem",
              borderRadius: uiTokens.radius.full,
              backgroundColor: uiTokens.color.semantic.background.default,
              boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.12), 0 1px 1px 0 rgba(0, 0, 0, 0.14), 0 2px 1px -1px rgba(0, 0, 0, 0.2)",
            },
            "& .MuiSwitch-track": {
              width: "2.125rem",
              height: "0.875rem",
              borderRadius: uiTokens.radius.full,
              backgroundColor: uiTokens.color.semantic.foreground.subtle,
              opacity: 1,
              alignSelf: "center",
            },
          },
        },
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        root: {
          "&.ui-toggle-label": {
            width: "fit-content",
            height: "1.25rem",
            gap: "0.5rem",
            margin: 0,
            "& .MuiFormControlLabel-label": {
              color: uiTokens.color.semantic.foreground.muted,
              fontSize: uiTokens.typography.body.sm.fontSize,
              fontStyle: "normal",
              fontWeight: uiTokens.fontWeight.normal,
              lineHeight: uiTokens.typography.body.sm.lineHeight,
              letterSpacing: 0,
            },
          },
          "&.ui-radio-label": {
            gap: "0.5rem",
            margin: 0,
            "& .MuiFormControlLabel-label": {
              color: uiTokens.color.semantic.foreground.muted,
              fontSize: uiTokens.typography.body.sm.fontSize,
              fontStyle: "normal",
              fontWeight: uiTokens.fontWeight.normal,
              lineHeight: uiTokens.typography.body.sm.lineHeight,
              letterSpacing: 0,
            },
          },
        },
        label: {
          color: uiTokens.color.primitive.neutral[800],
          fontSize: uiTokens.typography.body.sm.fontSize,
          lineHeight: uiTokens.typography.body.sm.lineHeight,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        rounded: {
          borderRadius: uiTokens.radius.xl,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: uiTokens.radius.xl,
          border: `1px solid ${uiTokens.color.semantic.border.default}`,
          boxShadow: uiTokens.shadow.sm,
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: uiTokens.radius.xl,
          boxShadow: uiTokens.shadow.lg,
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          borderBottom: `1px solid ${uiTokens.color.semantic.border.default}`,
        },
        indicator: {
          backgroundColor: uiTokens.color.semantic.primary.default,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: "none",
          fontWeight: 600,
          letterSpacing: 0,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: uiTokens.radius.full,
          fontWeight: uiTokens.fontWeight.medium,
          letterSpacing: 0,
        },
        sizeSmall: {
          height: "26px",
          minHeight: "26px",
          fontSize: uiTokens.typography.label.sm.fontSize,
          fontWeight: uiTokens.typography.label.sm.fontWeight,
          lineHeight: uiTokens.typography.label.sm.lineHeight,
          "& .MuiChip-label": {
            paddingLeft: uiTokens.space[12],
            paddingRight: uiTokens.space[12],
          },
        },
        sizeMedium: {
          height: "26px",
          minHeight: "26px",
          fontSize: uiTokens.typography.label.sm.fontSize,
          fontWeight: uiTokens.typography.label.sm.fontWeight,
          lineHeight: uiTokens.typography.label.sm.lineHeight,
          "& .MuiChip-label": {
            paddingLeft: uiTokens.space[12],
            paddingRight: uiTokens.space[12],
          },
        },
      },
    },
    MuiDataGrid: {
      styleOverrides: {
        root: {
          borderColor: uiTokens.color.semantic.border.default,
          borderRadius: uiTokens.radius["2xl"],
          backgroundColor: uiTokens.color.semantic.background.default,
          "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within":
            {
              outline: "none !important",
            },
          "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": {
            outline: "none !important",
          },
          "& .MuiDataGrid-row:focus": {
            outline: "none !important",
          },
          "& .MuiDataGrid-columnHeader button:focus": {
            outline: "none !important",
          },
        },
        columnHeaders: {
          backgroundColor: uiTokens.color.semantic.table.header,
          color: uiTokens.color.semantic.foreground.muted,
          fontWeight: uiTokens.fontWeight.medium,
          fontSize: uiTokens.typography.label.sm.fontSize,
          lineHeight: uiTokens.typography.label.sm.lineHeight,
          borderBottom: `1.11px solid ${uiTokens.color.semantic.border.default}`,
          borderRadius: 0,
        },
        row: {
          backgroundColor: uiTokens.color.semantic.table.row2,
        },
        cell: {
          color: uiTokens.color.semantic.foreground.default,
          fontSize: uiTokens.typography.body.sm.fontSize,
          fontWeight: uiTokens.typography.body.sm.fontWeight,
          lineHeight: uiTokens.typography.body.sm.lineHeight,
        },
      },
    },
  },
  typography: {
    fontFamily: fontFamilyBase,
    button: {
      ...uiTokens.typography.label.md,
      textTransform: "none",
      letterSpacing: 0,
    },
    h1: {
      ...uiTokens.typography.display.xl,
      letterSpacing: 0,
    },
    h2: {
      ...uiTokens.typography.display.lg,
      letterSpacing: 0,
    },
    h3: {
      ...uiTokens.typography.display.md,
      letterSpacing: 0,
    },
    h4: {
      ...uiTokens.typography.display.sm,
      letterSpacing: 0,
    },
    h5: {
      ...uiTokens.typography.heading.xl,
      letterSpacing: 0,
    },
    h6: {
      ...uiTokens.typography.heading.lg,
      letterSpacing: 0,
    },
    body1: {
      ...uiTokens.typography.body.md,
      letterSpacing: 0,
    },
    body2: {
      ...uiTokens.typography.body.sm,
      letterSpacing: 0,
    },
    subtitle1: {
      ...uiTokens.typography.heading.md,
      letterSpacing: 0,
    },
    subtitle2: {
      ...uiTokens.typography.label.md,
      letterSpacing: 0,
    },
    caption: {
      ...uiTokens.typography.body.xs,
      letterSpacing: 0,
    },
    overline: {
      ...uiTokens.typography.label.sm,
      textTransform: "uppercase",
      letterSpacing: 0,
    },
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: remToPx(uiTokens.breakpoint.sm), // 640px
      md: remToPx(uiTokens.breakpoint.md), // 768px
      lg: remToPx(uiTokens.breakpoint.lg), // 1024px
      xl: remToPx(uiTokens.breakpoint.xl), // 1280px
    },
  },
});

export default Theme;
